/**
 * Contextual Tooltip Component (Phase 2 — Step 2.14).
 *
 * Hover/focus tooltip with ARIA support.
 */

import React, { useState, useRef, useCallback, useId } from 'react';

interface TooltipProps {
  content: string;
  children: React.ReactElement;
}

export function Tooltip({ content, children }: TooltipProps): React.ReactElement {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();

  // Tracks the pointer type of the most recent gesture so touch taps can drive
  // visibility via click without conflicting with the hover/focus behavior used
  // by mouse and keyboard users.
  const lastPointerType = useRef<string>('');
  const suppressFocus = useRef(false);

  const show = useCallback(() => setVisible(true), []);
  const hide = useCallback(() => setVisible(false), []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    lastPointerType.current = e.pointerType;
    if (e.pointerType === 'touch') {
      // Suppress the focus-show that a touch tap would otherwise trigger; the
      // subsequent click handler toggles visibility for touch users.
      suppressFocus.current = true;
    }
  }, []);

  const handleFocus = useCallback(() => {
    if (suppressFocus.current) return;
    setVisible(true);
  }, []);

  const handleBlur = useCallback(() => {
    suppressFocus.current = false;
    setVisible(false);
  }, []);

  const handleClick = useCallback(() => {
    // Touch devices have no reliable hover/focus; tap toggles the tooltip.
    if (lastPointerType.current === 'touch') {
      setVisible((v) => !v);
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') setVisible(false);
    },
    [],
  );

  return (
    <span className="tooltip-wrapper" ref={triggerRef}>
      <span
        className="tooltip-trigger"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onKeyDown={handleKeyDown}
        aria-describedby={visible ? tooltipId : undefined}
        tabIndex={0}
        role="button"
      >
        {children}
      </span>
      {visible && (
        <span
          id={tooltipId}
          className="tooltip-content"
          role="tooltip"
        >
          {content}
        </span>
      )}
    </span>
  );
}
